import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  const handleNavigation = (sectionId) => {
    if (isHomePage) {
      // If we're on home page, scroll to section
      if (sectionId === 'home') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      // If we're on another page, navigate to home with hash
      if (sectionId === 'home') {
        navigate('/');
        // Scroll to top after navigation
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      } else {
        navigate(`/#${sectionId}`);
        // Scroll to section after navigation
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-blue-900 text-white relative overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ 
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` 
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-10">
          {/* Logo and Description - Reduced spacing */}
          <div className="space-y-4 md:col-span-1">
            <div className="flex items-center space-x-3 group">
              <div className="p-2 bg-blue-600 rounded-xl shadow-lg transform group-hover:scale-110 transition duration-300">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white">
                  <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 7L12 4.5L17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 17L12 19.5L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 7L12 12L22 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 22V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <Link to="/" className="text-xl font-bold tracking-tight text-white hover:text-blue-300 transition duration-300">
                  Akir <span className="text-blue-400">Advisory</span>
                </Link>
                <p className="text-blue-200 text-xs mt-1">Trusted Advisory Partners</p>
              </div>
            </div>
            <p className="text-blue-100 text-sm leading-relaxed">
              Empowering entrepreneurs with seamless legal and regulatory solutions.
            </p>
            <div className="flex space-x-2 pt-1">
              <div className="px-2 py-1 bg-blue-800 rounded-full text-xs text-blue-200 font-medium">✓ Trusted</div>
              <div className="px-2 py-1 bg-green-800 rounded-full text-xs text-green-200 font-medium">✓ Reliable</div>
            </div>
          </div>

          {/* Quick Links - Reduced spacing */}
          <div className="md:col-span-1">
            <h3 className="text-base font-bold text-white mb-4 flex items-center">
              <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
              Quick Navigation
            </h3>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => handleNavigation('home')}
                  className="group flex items-center text-blue-100 hover:text-white transition-all duration-300 hover:translate-x-1 w-full text-left"
                >
                  <span className="w-1 h-1 bg-blue-400 rounded-full mr-2 opacity-0 group-hover:opacity-100 transition duration-300"></span>
                  <span className="text-sm">Home</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('services')}
                  className="group flex items-center text-blue-100 hover:text-white transition-all duration-300 hover:translate-x-1 w-full text-left"
                >
                  <span className="w-1 h-1 bg-blue-400 rounded-full mr-2 opacity-0 group-hover:opacity-100 transition duration-300"></span>
                  <span className="text-sm">Our Services</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('impact')}
                  className="group flex items-center text-blue-100 hover:text-white transition-all duration-300 hover:translate-x-1 w-full text-left"
                >
                  <span className="w-1 h-1 bg-blue-400 rounded-full mr-2 opacity-0 group-hover:opacity-100 transition duration-300"></span>
                  <span className="text-sm">Our Impact</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('about')}
                  className="group flex items-center text-blue-100 hover:text-white transition-all duration-300 hover:translate-x-1 w-full text-left"
                >
                  <span className="w-1 h-1 bg-blue-400 rounded-full mr-2 opacity-0 group-hover:opacity-100 transition duration-300"></span>
                  <span className="text-sm">About Us</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('contact')}
                  className="group flex items-center text-blue-100 hover:text-white transition-all duration-300 hover:translate-x-1 w-full text-left"
                >
                  <span className="w-1 h-1 bg-blue-400 rounded-full mr-2 opacity-0 group-hover:opacity-100 transition duration-300"></span>
                  <span className="text-sm">Get In Touch</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Get In Touch - Reduced spacing */}
          <div className="md:col-span-1">
            <h3 className="text-base font-bold text-white mb-4 flex items-center">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
              Connect With Us
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start group">
                <div className="p-1.5 bg-blue-700 rounded-lg mr-3 group-hover:bg-blue-600 transition duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-200">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <div>
                  <p className="text-blue-200 text-xs font-semibold">Email Us</p>
                  <a href="mailto:info@akiradvisory.in" className="text-white hover:text-blue-300 transition duration-300 text-sm font-medium">info@akiradvisory.in</a>
                </div>
              </li>
              <li className="flex items-start group">
                <div className="p-1.5 bg-green-700 rounded-lg mr-3 group-hover:bg-green-600 transition duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-200">
                    <path d="M22 16.92v3.08a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-5.6-5.6A19.79 19.79 0 0 1 2 4.18 2 2 0 0 1 4.1 2h3.08a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-blue-200 text-xs font-semibold">Call Us</p>
                  <a href="tel:+919873030848" className="text-white hover:text-green-300 transition duration-300 text-sm font-medium">+91-9873030848</a>
                </div>
              </li>
              <li className="flex items-start group">
                <div className="p-1.5 bg-purple-700 rounded-lg mr-3 group-hover:bg-purple-600 transition duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-purple-200">
                    <path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                </div>
                <div>
                  <p className="text-blue-200 text-xs font-semibold">Visit Us</p>
                  <p className="text-white text-sm">357, Surya Nagar, Phase-II<br/>Faridabad, Haryana</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Follow Us & Location - Reduced spacing */}
          <div className="md:col-span-1">
            <h3 className="text-base font-bold text-white mb-4 flex items-center">
              <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
              Find & Follow Us
            </h3>
            
            {/* Social Media - Reduced size */}
            <div className="mb-4">
              <p className="text-blue-200 text-xs font-semibold mb-3">Stay Connected</p>
              <div className="flex space-x-3">
                <a href="https://twitter.com/akiradvisory" target="_blank" rel="noopener noreferrer" className="p-2.5 bg-blue-800 hover:bg-blue-700 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg group">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 text-blue-300 group-hover:text-white">
                    <path d="M18.901 1.153h3.682l-8.082 9.074 9.479 12.012h-7.653l-6.529-8.245-7.794 8.245h-3.682l8.472-8.995-9.255-10.09h7.73l5.808 7.398zm-.416 19.341h2.247L6.649 3.398H4.269l14.216 17.096z"/>
                  </svg>
                </a>
                <a href="https://facebook.com/akiradvisory" target="_blank" rel="noopener noreferrer" className="p-2.5 bg-blue-800 hover:bg-blue-700 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg group">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 text-blue-300 group-hover:text-white">
                    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.588-1.333h2.412v-3h-3.473c-4.078 0-4.527 1.026-4.527 3.291v1.709z"/>
                  </svg>
                </a>
                <a href="https://linkedin.com/company/akir-advisory" target="_blank" rel="noopener noreferrer" className="p-2.5 bg-blue-800 hover:bg-blue-700 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg group">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 text-blue-300 group-hover:text-white">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.153-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Google Maps - FIXED with proper embed URL */}
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white font-semibold text-xs">Our Location</p>
                <a href="https://www.google.com/maps/place/28%C2%B028'49.3%22N+77%C2%B019'57.2%22E/@28.4803579,77.3299688,999m/data=!3m2!1e3!4b1!4m4!3m3!8m2!3d28.4803579!4d77.3325437!5m1!1e1?hl=en&entry=ttu&g_ep=EgoyMDI1MTExNy4wIKXMDSoASAFQAw%3D%3D" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="text-xs text-blue-300 hover:text-white transition duration-300 flex items-center">
                  Open Map
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ml-1">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </a>
              </div>
              <div className="rounded-lg overflow-hidden shadow-xl border border-white/20">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d28037.13296918177!2d77.3299688!3d28.4803579!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjjCsDI4JzQ5LjMiTiA3N8KwMTknNTcuMiJF!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin" 
                  width="100%" 
                  height="100" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Akir Advisory Location"
                  className="w-full"
                >
                </iframe>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright Section - Reduced spacing */}
        <div className="mt-12 pt-6 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0">
            <p className="text-blue-200 text-xs text-center md:text-left">
              &copy; {currentYear} Akir Advisory. All rights reserved. 
              <span className="text-white/60 mx-2">|</span> Empowering Businesses Since 2017
            </p>
            <div className="flex space-x-4 text-blue-200 text-xs">
              <button className="hover:text-white transition duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
                Privacy Policy
              </button>
              <button className="hover:text-white transition duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
                Terms of Service
              </button>
              <button className="hover:text-white transition duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
                Disclaimer
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;